import { supabase } from './supabase';
import { toast } from 'react-toastify';
import { Database } from '../types/database';

type CommentInsert = Database['public']['Tables']['comments']['Insert'];

export interface CommentWithProfile {
  id: string;
  content: string;
  author: {
    id: string;
    name: string;
    email: string;
    avatar: string;
  };
  mentions: string[];
  created_at: string;
  updated_at: string;
  idea_id?: string;
  task_id?: string;
}

/**
 * Add a new comment to an idea or task
 */
export const addComment = async (
  content: string,
  ideaId?: string,
  taskId?: string,
  mentions: string[] = []
): Promise<CommentWithProfile | null> => {
  try {
    // Validate input
    if (!content.trim()) {
      toast.error('Comment content cannot be empty');
      return null;
    }

    if (!ideaId && !taskId) {
      toast.error('Comment must be associated with an idea or task');
      return null;
    }

    if (ideaId && taskId) {
      toast.error('Comment cannot be associated with both an idea and task');
      return null;
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      toast.error('You must be logged in to add comments');
      return null;
    }

    // Prepare comment data
    const commentData: CommentInsert = {
      content: content.trim(),
      author_id: user.id,
      mentions: mentions,
      ...(ideaId && { idea_id: ideaId }),
      ...(taskId && { task_id: taskId }),
    };

    // Insert comment
    const { data, error } = await supabase
      .from('comments')
      .insert(commentData)
      .select(`
        *,
        author:profiles!comments_author_id_fkey (
          id,
          name,
          email,
          avatar
        )
      `)
      .single();

    if (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
      return null;
    }

    // Transform data to match our interface
    const transformedComment: CommentWithProfile = {
      id: data.id,
      content: data.content,
      author: {
        id: data.author.id,
        name: data.author.name,
        email: data.author.email,
        avatar: data.author.avatar || '👤',
      },
      mentions: data.mentions,
      created_at: data.created_at,
      updated_at: data.updated_at,
      idea_id: data.idea_id || undefined,
      task_id: data.task_id || undefined,
    };

    toast.success('Comment added successfully');
    return transformedComment;
  } catch (error) {
    console.error('Error adding comment:', error);
    toast.error('Failed to add comment');
    return null;
  }
};

/**
 * Get all comments for an idea
 */
export const getIdeaComments = async (ideaId: string): Promise<CommentWithProfile[]> => {
  try {
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        author:profiles!comments_author_id_fkey (
          id,
          name,
          email,
          avatar
        )
      `)
      .eq('idea_id', ideaId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching idea comments:', error);
      return [];
    }

    return data.map(comment => ({
      id: comment.id,
      content: comment.content,
      author: {
        id: comment.author.id,
        name: comment.author.name,
        email: comment.author.email,
        avatar: comment.author.avatar || '👤',
      },
      mentions: comment.mentions,
      created_at: comment.created_at,
      updated_at: comment.updated_at,
      idea_id: comment.idea_id || undefined,
      task_id: comment.task_id || undefined,
    }));
  } catch (error) {
    console.error('Error fetching idea comments:', error);
    return [];
  }
};

/**
 * Get all comments for a task
 */
export const getTaskComments = async (taskId: string): Promise<CommentWithProfile[]> => {
  try {
    const { data, error } = await supabase
      .from('comments')
      .select(`
        *,
        author:profiles!comments_author_id_fkey (
          id,
          name,
          email,
          avatar
        )
      `)
      .eq('task_id', taskId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching task comments:', error);
      return [];
    }

    return data.map(comment => ({
      id: comment.id,
      content: comment.content,
      author: {
        id: comment.author.id,
        name: comment.author.name,
        email: comment.author.email,
        avatar: comment.author.avatar || '👤',
      },
      mentions: comment.mentions,
      created_at: comment.created_at,
      updated_at: comment.updated_at,
      idea_id: comment.idea_id || undefined,
      task_id: comment.task_id || undefined,
    }));
  } catch (error) {
    console.error('Error fetching task comments:', error);
    return [];
  }
};

/**
 * Delete a comment (only by the author)
 */
export const deleteComment = async (commentId: string): Promise<boolean> => {
  try {
    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      toast.error('You must be logged in to delete comments');
      return false;
    }

    // Check if user is the author of the comment
    const { data: comment, error: fetchError } = await supabase
      .from('comments')
      .select('author_id')
      .eq('id', commentId)
      .single();

    if (fetchError || !comment) {
      toast.error('Comment not found');
      return false;
    }

    if (comment.author_id !== user.id) {
      toast.error('You can only delete your own comments');
      return false;
    }

    // Delete the comment
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
      return false;
    }

    toast.success('Comment deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting comment:', error);
    toast.error('Failed to delete comment');
    return false;
  }
};

/**
 * Update a comment (only by the author)
 */
export const updateComment = async (
  commentId: string,
  content: string,
  mentions: string[] = []
): Promise<CommentWithProfile | null> => {
  try {
    if (!content.trim()) {
      toast.error('Comment content cannot be empty');
      return null;
    }

    // Get current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      toast.error('You must be logged in to update comments');
      return null;
    }

    // Check if user is the author of the comment
    const { data: comment, error: fetchError } = await supabase
      .from('comments')
      .select('author_id')
      .eq('id', commentId)
      .single();

    if (fetchError || !comment) {
      toast.error('Comment not found');
      return null;
    }

    if (comment.author_id !== user.id) {
      toast.error('You can only update your own comments');
      return null;
    }

    // Update the comment
    const { data, error } = await supabase
      .from('comments')
      .update({
        content: content.trim(),
        mentions: mentions,
      })
      .eq('id', commentId)
      .select(`
        *,
        author:profiles!comments_author_id_fkey (
          id,
          name,
          email,
          avatar
        )
      `)
      .single();

    if (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
      return null;
    }

    // Transform data to match our interface
    const transformedComment: CommentWithProfile = {
      id: data.id,
      content: data.content,
      author: {
        id: data.author.id,
        name: data.author.name,
        email: data.author.email,
        avatar: data.author.avatar || '👤',
      },
      mentions: data.mentions,
      created_at: data.created_at,
      updated_at: data.updated_at,
      idea_id: data.idea_id || undefined,
      task_id: data.task_id || undefined,
    };

    toast.success('Comment updated successfully');
    return transformedComment;
  } catch (error) {
    console.error('Error updating comment:', error);
    toast.error('Failed to update comment');
    return null;
  }
};

/**
 * Extract mentions from comment content
 */
export const extractMentions = (content: string): string[] => {
  const mentionRegex = /@(\w+)/g;
  const mentions = [];
  let match;
  
  while ((match = mentionRegex.exec(content)) !== null) {
    mentions.push(match[1]);
  }
  
  return Array.from(new Set(mentions)); // Remove duplicates
};

/**
 * Format comment content with mentions highlighting
 */
export const formatCommentContent = (content: string): string => {
  return content.replace(
    /@(\w+)/g,
    '<span class="text-blue-600 font-medium">@$1</span>'
  );
};

/**
 * Get mention suggestions (could be enhanced with actual user search)
 */
export const getMentionSuggestions = async (query: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('name')
      .ilike('name', `%${query}%`)
      .limit(10);

    if (error) {
      console.error('Error fetching mention suggestions:', error);
      return [];
    }

    return data.map(profile => profile.name);
  } catch (error) {
    console.error('Error fetching mention suggestions:', error);
    return [];
  }
}; 